import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Edit, Trash2, Save, X, Image as ImageIcon, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../hooks/use-toast';
import adminApi from '../../lib/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductsManager = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const loadProducts = useCallback(async () => {
    try {
      const response = await adminApi.get('/api/admin/products');
      setProducts(response.data);
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de charger les produits", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const handleDelete = async (productId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;
    try {
      await adminApi.delete(`/api/admin/products/${productId}`);
      toast({ title: "Produit supprimé avec succès" });
      loadProducts();
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de supprimer le produit", variant: "destructive" });
    }
  };

  const handleSave = async (productData) => {
    try {
      if (productData.id && editingProduct) {
        await adminApi.put(`/api/admin/products/${productData.id}`, productData);
        toast({ title: "Produit mis à jour avec succès !" });
      } else {
        await adminApi.post('/api/admin/products', productData);
        toast({ title: "Produit créé avec succès !" });
      }
      setEditingProduct(null);
      setShowForm(false);
      loadProducts();
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Impossible de sauvegarder", variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-400 text-lg">Chargement des produits...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Gestion des Produits</h1>
          <p className="text-gray-400">Gérez vos produits LED en toute simplicité</p>
        </div>
        <Button
          onClick={() => {
            setEditingProduct(null);
            setShowForm(true);
          }}
          className="bg-orange-600 hover:bg-orange-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Produit
        </Button>
      </div>

      {showForm && (
        <ProductForm
          product={editingProduct}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingProduct(null);
          }}
        />
      )}

      <div className="grid grid-cols-1 gap-4">
        {products.map((product) => (
          <div
            key={product.id}
            className="bg-gray-900 rounded-lg p-6 border border-gray-800 hover:border-gray-700 transition-colors"
          >
            <div className="flex items-start gap-4">
              {product.images?.[0] && (
                <img
                  src={product.images[0]}
                  alt={product.name}
                  className="w-24 h-24 object-cover rounded-lg"
                />
              )}
              <div className="flex-1">
                <h3 className="text-white font-semibold text-lg mb-1">{product.name}</h3>
                <p className="text-gray-400 text-sm mb-2 line-clamp-2">{product.description}</p>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-400">Catégorie: <span className="text-purple-400">{product.category}</span></span>
                  <span className="text-gray-400">Stock: <span className={product.stock < 20 ? 'text-red-500 font-semibold' : 'text-green-500 font-semibold'}>{product.stock}</span></span>
                  <span className="text-gray-400">Ordre: <span className="text-purple-400 font-semibold">{product.sortOrder || 0}</span></span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-orange-400 font-bold text-2xl mb-2">{product.price.toLocaleString('fr-FR')} €</p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingProduct(product);
                      setShowForm(true);
                    }}
                    className="border-gray-700 text-white hover:bg-gray-800"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Modifier
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(product.id)}
                    className="border-red-700 text-red-500 hover:bg-red-900"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {products.length === 0 && (
        <div className="text-center py-20 bg-gray-900 rounded-lg border border-gray-800">
          <ImageIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg mb-4">Aucun produit pour le moment</p>
          <Button onClick={() => setShowForm(true)} className="bg-orange-600 hover:bg-orange-700">
            Créer votre premier produit
          </Button>
        </div>
      )}
    </div>
  );
};

const ProductForm = ({ product, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    slug: product?.slug || '',
    price: product?.price || 0,
    priceAED: product?.priceAED ?? '',
    currency: product?.currency || 'EUR',
    images: product?.images || [''],
    category: product?.category || 'produits-led',
    description: product?.description || '',
    features: product?.features || [''],
    stock: product?.stock || 0,
    minOrder: product?.minOrder || null,
    sortOrder: product?.sortOrder || 0,
    id: product?.id || undefined
  });

  const [imageInput, setImageInput] = useState('');
  const [featureInput, setFeatureInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.slug || !formData.price) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires",
        variant: "destructive"
      });
      return;
    }

    const cleanData = {
      ...formData,
      images: formData.images.filter(img => img.trim()),
      features: formData.features.filter(f => f.trim()),
      price: parseFloat(formData.price),
      priceAED: formData.priceAED === '' || formData.priceAED === null ? null : parseFloat(formData.priceAED),
      stock: parseInt(formData.stock),
      minOrder: formData.minOrder ? parseInt(formData.minOrder) : null,
      sortOrder: parseInt(formData.sortOrder) || 0
    };

    if (cleanData.images.length === 0) {
      toast({
        title: "Erreur",
        description: "Ajoutez au moins une image",
        variant: "destructive"
      });
      return;
    }

    onSave(cleanData);
  };

  const addImage = () => {
    if (imageInput.trim()) {
      setFormData({ ...formData, images: [...formData.images, imageInput] });
      setImageInput('');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await adminApi.post('/api/admin/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      setFormData(prev => ({ ...prev, images: [...prev.images, imageUrl] }));
      toast({ title: "Image uploadée avec succès" });
    } catch (error) {
      toast({
        title: "Erreur d'upload",
        description: error.response?.data?.detail || "Impossible d'uploader l'image",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData({ ...formData, features: [...formData.features, featureInput] });
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: newFeatures });
  };

  const generateSlug = () => {
    const slug = formData.name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    setFormData({ ...formData, slug });
  };

  return (
    <div className="bg-gray-900 rounded-lg p-8 border border-gray-800 mb-8">
      <h2 className="text-2xl font-bold text-white mb-6">
        {product ? '✏️ Modifier le produit' : '➕ Nouveau produit'}
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Nom et Slug */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Nom du produit *
            </label>
            <Input
              placeholder="Bracelets LED Ajustables"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="bg-gray-800 text-white border-gray-700"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Slug (URL) *
            </label>
            <div className="flex gap-2">
              <Input
                placeholder="bracelets-led-ajustables"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                required
                className="bg-gray-800 text-white border-gray-700"
              />
              <Button type="button" onClick={generateSlug} variant="outline" className="border-gray-700 text-white">
                Auto
              </Button>
            </div>
          </div>
        </div>

        {/* Prix, Stock, Ordre, Catégorie */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Prix (€) *
            </label>
            <Input
              type="number"
              step="0.01"
              placeholder="99.99"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              required
              className="bg-gray-800 text-white border-gray-700"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Prix AED (facultatif)
            </label>
            <Input
              type="number"
              step="1"
              placeholder="calculé par le taux"
              value={formData.priceAED}
              onChange={(e) => setFormData({ ...formData, priceAED: e.target.value })}
              className="bg-gray-800 text-white border-gray-700"
              data-testid="product-price-aed"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Stock *
            </label>
            <Input
              type="number"
              placeholder="100"
              value={formData.stock}
              onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
              required
              className="bg-gray-800 text-white border-gray-700"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Ordre d'affichage
            </label>
            <Input
              type="number"
              placeholder="1"
              value={formData.sortOrder}
              onChange={(e) => setFormData({ ...formData, sortOrder: e.target.value })}
              className="bg-gray-800 text-white border-gray-700"
              data-testid="product-sort-order"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Catégorie *
            </label>
            <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
              <SelectTrigger className="bg-gray-800 text-white border-gray-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="produits-led">Produits LED</SelectItem>
                <SelectItem value="controleurs">Contrôleurs</SelectItem>
                <SelectItem value="kit-led-fete">Kits LED</SelectItem>
                <SelectItem value="party-led-kits">Party LED Kits</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Description *
          </label>
          <Textarea
            placeholder="Description détaillée du produit..."
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
            rows={4}
            className="bg-gray-800 text-white border-gray-700"
          />
        </div>

        {/* Images */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Images *
          </label>
          <div className="space-y-3">
            {formData.images.map((img, index) => (
              img && (
                <div key={`img-${index}-${img.substring(img.length - 10)}`} className="flex items-center gap-2 bg-gray-800 p-3 rounded">
                  <img src={img} alt="" className="w-16 h-16 object-cover rounded" onError={(e) => e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>'} />
                  <span className="flex-1 text-white text-sm truncate">{img.split('/').pop()}</span>
                  <Button type="button" size="sm" variant="outline" onClick={() => removeImage(index)} className="border-red-700 text-red-500">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )
            ))}
            
            {/* Upload button */}
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="image-file-input"
              />
              <Button 
                type="button" 
                onClick={() => fileInputRef.current?.click()} 
                disabled={uploading}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="upload-image-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Upload en cours...' : 'Uploader une image'}
              </Button>
            </div>
            
            {/* URL input (alternative) */}
            <div className="flex gap-2">
              <Input
                placeholder="Ou collez une URL d'image..."
                value={imageInput}
                onChange={(e) => setImageInput(e.target.value)}
                className="bg-gray-800 text-white border-gray-700"
                data-testid="image-url-input"
              />
              <Button type="button" onClick={addImage} variant="outline" className="border-gray-700 text-white">
                <Plus className="w-4 h-4 mr-1" />
                URL
              </Button>
            </div>
          </div>
        </div>

        {/* Features */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Caractéristiques
          </label>
          <div className="space-y-2">
            {formData.features.map((feature, index) => (
              feature && (
                <div key={`feat-${index}-${feature.substring(0, 10)}`} className="flex items-center gap-2">
                  <Input value={feature} readOnly className="flex-1 bg-gray-800 text-white border-gray-700" />
                  <Button type="button" size="sm" variant="outline" onClick={() => removeFeature(index)} className="border-red-700 text-red-500">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )
            ))}
            <div className="flex gap-2">
              <Input
                placeholder="Nouvelle caractéristique"
                value={featureInput}
                onChange={(e) => setFeatureInput(e.target.value)}
                className="bg-gray-800 text-white border-gray-700"
              />
              <Button type="button" onClick={addFeature} variant="outline" className="border-gray-700 text-white">
                <Plus className="w-4 h-4 mr-1" />
                Ajouter
              </Button>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4 pt-4">
          <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700 py-3">
            <Save className="w-4 h-4 mr-2" />
            {product ? 'Mettre à jour' : 'Créer le produit'}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel} className="border-gray-700 text-white hover:bg-gray-800">
            <X className="w-4 h-4 mr-2" />
            Annuler
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProductsManager;
